/**
 * GA4 电商事件（begin_checkout / purchase）。
 * 与 Logstore 220019 / 220014 并行；币种固定 USD，金额用 plan.price_usd。
 */
import { isGaEnabled } from '~/utils/analyticsEnv.js'
import { resolveLipSyncUserId } from '~/utils/lipsyncAnalytics.js'
import {
  buildAnalyticsPlanPayload,
  readPayPlanClickContext,
} from '~/utils/pricingAnalytics.js'
import {
  BILLING_MODES,
  DEFAULT_SLIDER_INDEX,
  getOnetimeCards,
  getPricingCards,
} from '~/constants/pricingPlans.js'

export const GA_CURRENCY = 'USD'
export const GTAG_READY_EVENT = 'lip:gtag-ready'

const GTAG_READY_TIMEOUT_MS = 12_000

/** @type {Array<() => void>} */
const pendingReadyCallbacks = []
let gtagReadyNotified = false

function canUseGa(config) {
  return isGaEnabled(config)
}

export function notifyGtagReady() {
  if (!import.meta.client || gtagReadyNotified) return
  gtagReadyNotified = true
  const queue = pendingReadyCallbacks.splice(0, pendingReadyCallbacks.length)
  for (const cb of queue) {
    try {
      cb()
    } catch (error) {
      console.warn('[ga] ready callback failed', error)
    }
  }
  try {
    window.dispatchEvent(new CustomEvent(GTAG_READY_EVENT))
  } catch {
    /* ignore */
  }
}

/**
 * 等 deferred gtag 完成 `config` 后再执行；超时未就绪则丢弃（本地/dev 本身 isGaEnabled=false）。
 * 注意：stub 注入后 `gtag` 已是函数，但不能当作 ready（需等 measurement config）。
 * @param {() => void} callback
 * @param {{ timeoutMs?: number, config?: { public?: Record<string, unknown> } }} [options]
 */
export function whenGtagReady(callback, options = {}) {
  if (!import.meta.client) return
  const { timeoutMs = GTAG_READY_TIMEOUT_MS, config } = options
  if (!canUseGa(config)) return

  if (gtagReadyNotified && typeof globalThis.gtag === 'function') {
    callback()
    return
  }

  pendingReadyCallbacks.push(callback)
  window.setTimeout(() => {
    const idx = pendingReadyCallbacks.indexOf(callback)
    if (idx >= 0) pendingReadyCallbacks.splice(idx, 1)
  }, timeoutMs)
}

/**
 * @param {string} name
 * @param {Record<string, unknown>} [params]
 * @param {{ public?: Record<string, unknown> }} [config]
 */
export function trackGaEvent(name, params = {}, config) {
  if (!import.meta.client) return
  if (!canUseGa(config)) return
  whenGtagReady(() => {
    if (typeof globalThis.gtag !== 'function') return
    globalThis.gtag('event', name, params)
  }, { config })
}

/** @param {Record<string, unknown> | null | undefined} planPayload */
export function buildGaItemsFromPlan(planPayload) {
  if (!planPayload) return []
  const itemId = String(planPayload.price_code || planPayload.sku_key || '')
  if (!itemId && !planPayload.plan_name) return []
  const price = Number(planPayload.price_usd) || 0
  return [
    {
      item_id: itemId || String(planPayload.sku_key || 'unknown'),
      item_name: String(planPayload.plan_name || planPayload.sku_key || 'plan'),
      item_category: String(planPayload.billing || ''),
      item_variant: String(planPayload.tier || ''),
      price,
      quantity: 1,
    },
  ]
}

function resolvePlanPayloadFromSelection(selection, productIds = {}) {
  if (!selection?.tier || !selection?.billing) return null

  const billing = selection.billing
  const sliderState = {
    pro: DEFAULT_SLIDER_INDEX,
    ultra: DEFAULT_SLIDER_INDEX,
    ...(selection.sliderStatePatch || {}),
  }

  const cards =
    billing === BILLING_MODES.onetime
      ? getOnetimeCards(productIds)
      : getPricingCards(billing, sliderState, productIds)

  const bySku = selection.skuKey
    ? cards.find((card) => card.key === selection.skuKey)
    : null
  const byTier = cards.find((card) => card.tier === selection.tier)
  return buildAnalyticsPlanPayload(bySku || byTier || null)
}

/**
 * @param {{ page_path?: string, route?: { path?: string }, userStore?: unknown, user_id?: string }} [ctx]
 */
export function resolveGaPagePath(ctx = {}) {
  if (ctx.page_path) return String(ctx.page_path)
  if (ctx.route?.path) return String(ctx.route.path)
  if (import.meta.client) return window.location.pathname || ''
  return ''
}

/**
 * 登录 user_id 或匿名 uuid（与 Logstore 一致；不传邮箱）。
 * @param {{ userStore?: unknown, user_id?: string }} [ctx]
 */
export function resolveGaUserId(ctx = {}) {
  if (ctx.user_id) return String(ctx.user_id)
  return resolveLipSyncUserId(ctx.userStore) || ''
}

function withIdentityParams(params, { page_path, user_id } = {}) {
  return {
    ...params,
    ...(page_path ? { page_path } : {}),
    ...(user_id ? { user_id } : {}),
  }
}

/**
 * 支付成功：优先最近一次 Buy Now 上下文，否则用 selection 反查标价。
 * @param {{ selection?: Record<string, unknown> | null, productIds?: Record<string, string> }} [ctx]
 */
export function resolveGaPurchasePlanContext(ctx = {}) {
  const stored = readPayPlanClickContext()
  const page_path = stored?.page_path ? String(stored.page_path) : ''

  if (Number(stored?.plan?.price_usd) > 0) {
    return {
      planPayload: stored.plan,
      surface: stored.surface,
      page_path,
    }
  }

  const fromSelection = resolvePlanPayloadFromSelection(ctx.selection, ctx.productIds)
  if (Number(fromSelection?.price_usd) > 0) {
    return {
      planPayload: fromSelection,
      surface: stored?.surface,
      page_path,
    }
  }

  return {
    planPayload: stored?.plan ?? fromSelection,
    surface: stored?.surface,
    page_path,
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} planPayload
 * @param {{
 *   surface?: string,
 *   page_path?: string,
 *   user_id?: string,
 *   userStore?: unknown,
 *   route?: { path?: string },
 *   config?: { public?: Record<string, unknown> },
 * }} [options]
 */
export function trackBeginCheckout(planPayload, options = {}) {
  const value = Number(planPayload?.price_usd) || 0
  if (!planPayload || value <= 0) {
    console.warn('[ga] begin_checkout skipped: missing price_usd', planPayload)
    return
  }

  const items = buildGaItemsFromPlan(planPayload)
  const page_path = resolveGaPagePath(options)
  const user_id = resolveGaUserId(options)
  trackGaEvent(
    'begin_checkout',
    withIdentityParams(
      {
        currency: GA_CURRENCY,
        value,
        items,
        ...(options.surface ? { surface: options.surface } : {}),
      },
      { page_path, user_id },
    ),
    options.config,
  )
}

/**
 * @param {{
 *   transactionId: string,
 *   planPayload?: Record<string, unknown> | null,
 *   surface?: string,
 *   page_path?: string,
 *   user_id?: string,
 *   userStore?: unknown,
 *   route?: { path?: string },
 *   config?: { public?: Record<string, unknown> },
 * }} params
 */
export function trackPurchase({
  transactionId,
  planPayload,
  surface,
  page_path: pagePathOpt,
  user_id: userIdOpt,
  userStore,
  route,
  config,
} = {}) {
  const id = String(transactionId || '').trim()
  const value = Number(planPayload?.price_usd) || 0
  if (!id) {
    console.warn('[ga] purchase skipped: missing transaction_id')
    return
  }
  if (!planPayload || value <= 0) {
    console.warn('[ga] purchase skipped: missing price_usd', { transactionId: id, planPayload })
    return
  }

  const items = buildGaItemsFromPlan(planPayload)
  const page_path = resolveGaPagePath({ page_path: pagePathOpt, route })
  const user_id = resolveGaUserId({ user_id: userIdOpt, userStore })
  trackGaEvent(
    'purchase',
    withIdentityParams(
      {
        transaction_id: id,
        currency: GA_CURRENCY,
        value,
        items,
        ...(surface ? { surface } : {}),
      },
      { page_path, user_id },
    ),
    config,
  )
}
